<template>
  <div>
    <DatatableReportV3
      v-if="initialData"
      title="Membership Service Utilization"
      download-url="/reports/service_utilization"
      :source="initialData.source"
      :config="config"
      :filters="initialData.filters"
      @filter-applied="onFilterApplied"
      @filter-removed="onFilterRemoved"
      @filters-cleared="onFiltersCleared"
    />
    <AppointmentCreateModal
      v-if="scheduleVisitContext"
      :prefilled_customer="scheduleVisitContext.customer"
      :prefilled_address="scheduleVisitContext.address"
      :membership_program_item_id="
        scheduleVisitContext.membership_program_item_id
      "
      :task_name="scheduleVisitContext.task_name"
      :scheduling_source="scheduleVisitContext.scheduling_source"
      @hide-modal="scheduleVisitContext = null"
      @created="onAppointmentCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { customerClassificationColumns } from "@/lib/customerTypes";
import { EventBus } from "@/lib/event_bus.js";
import { trackEvent } from "@/lib/pendo";

import Badge from "@/components/Badge.vue";
import { Filter } from "@/components/DatatableV3.vue";
import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import ScheduleVisitButton, {
  type ScheduleVisitEventPayload
} from "@/AppointmentCreateModal/ScheduleVisitButton.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";

interface InitialPageData {
  source: string;
  filters: Filter[];
}

interface FilterAppliedPayload {
  filter_name: string;
  filter_value: unknown;
  filter_value_count?: number;
}

interface FilterRemovedPayload {
  filter_name: string;
}

interface FiltersClearedPayload {
  filters_cleared_count: number;
}

const initialData = ref<InitialPageData | null>(null);
const scheduleVisitContext = ref<ScheduleVisitEventPayload | null>(null);
const router = useRouter();

const onFilterApplied = (payload: FilterAppliedPayload) => {
  trackEvent("service_utilization_filter_applied", payload);
};

const onFilterRemoved = (payload: FilterRemovedPayload) => {
  trackEvent("service_utilization_filter_removed", payload);
};

const onFiltersCleared = (payload: FiltersClearedPayload) => {
  trackEvent("service_utilization_filters_cleared", payload);
};

const handleOpenScheduleVisitModal = (payload: ScheduleVisitEventPayload) => {
  scheduleVisitContext.value = payload;
};

const onAppointmentCreated = (job: { id?: number } | undefined) => {
  scheduleVisitContext.value = null;
  EventBus.$emit("refresh-datatable");
  if (job?.id) {
    router.push(`/jobs/${job.id}`);
  }
};

const config = {
  responsive: true,
  // Default sort is Available until ascending — shows the most urgent rows
  // first so the admin doesn't have to paginate to triage.
  order: [[1, "asc"]],
  language: {
    emptyTable: "No services available"
  },
  createdRow: (row: HTMLElement, _data: unknown, dataIndex: number) => {
    row.setAttribute("data-cy", `service-utilization-row-${dataIndex}`);
  },
  vueComponents: [
    { rootId: "badge-vue", component: Badge },
    { rootId: "tag-list-vue", component: TagList },
    {
      rootId: "schedule-visit-button-vue",
      component: ScheduleVisitButton
    }
  ],
  columns: [
    {
      data: "status",
      title: "Status",
      searchable: false,
      responsivePriority: 2
    },
    {
      data: "available_until",
      title: "Available until",
      searchable: false,
      filterName: "ends_at",
      responsivePriority: 3
    },
    ...customerClassificationColumns(),
    {
      data: "customer",
      title: "Customer",
      aggregateTitle: "# of Customers",
      responsivePriority: 1
    },
    {
      data: "customer_tags",
      title: "Customer Tags",
      orderable: false,
      searchable: false,
      filterName: "customer_tags",
      responsivePriority: 15
    },
    {
      data: "task",
      title: "Task",
      orderable: false,
      searchable: false,
      filterName: "line_item_ids",
      headerClassName: "su-task-filter-column",
      responsivePriority: 4
    },
    {
      data: "service_address",
      title: "Service Address",
      responsivePriority: 5
    },
    { data: "zip", title: "Zip", filterName: "zipcode", responsivePriority: 6 },
    {
      data: "completion_date",
      title: "Completion Date",
      searchable: false,
      responsivePriority: 9
    },
    {
      data: "last_service_date",
      title: "Last Service Date",
      orderable: false,
      searchable: false,
      filterName: "last_service_date",
      responsivePriority: 8
    },
    {
      data: "invoice",
      title: "Invoice",
      orderable: false,
      searchable: false,
      responsivePriority: 10
    },
    { data: "program", title: "Program", responsivePriority: 7 },
    {
      data: "equipment",
      title: "Equipment",
      orderable: false,
      searchable: false,
      filterName: "component_types",
      responsivePriority: 12
    },
    {
      data: "equipment_age",
      title: "Equipment Age",
      orderable: false,
      searchable: false,
      filterName: "age_between",
      responsivePriority: 13
    },
    { data: "membership_id", title: "Membership ID", responsivePriority: 11 },
    {
      data: "covered_systems",
      title: "Covered Systems",
      orderable: false,
      searchable: false,
      filterName: "covered_systems",
      responsivePriority: 14
    },
    {
      data: "actions",
      title: "",
      orderable: false,
      searchable: false
    }
  ]
};

onMounted(async () => {
  EventBus.$on("open-schedule-visit-modal", handleOpenScheduleVisitModal);
  const res = await fetch(
    `${window.location.origin}/reports/service_utilization/initial_page_data`,
    { method: "GET" }
  );
  initialData.value = await res.json();
});

onUnmounted(() => {
  EventBus.$off("open-schedule-visit-modal", handleOpenScheduleVisitModal);
});
</script>

<style lang="scss">
// The controller sets autoWidth: false on the line_item_filter for this
// report, so the Select fills its column container instead of measuring
// the longest task label against the viewport. This cap keeps the Task
// column from expanding when a verbose task is selected; the pill chip
// styling itself lives on the shared Select component.
th.su-task-filter-column {
  max-width: 260px;
}
</style>
